import { queryOptions } from "@tanstack/react-query";
import type { MatchingProblemTag } from "@/types/matching";
import { apiBase, apiUrl } from "@/lib/api-url";

export const therapistsQueryOptions = (search?: string) =>
  queryOptions({
    queryKey: ["therapists", { search }],
    queryFn: async () => {
      const url = new URL("/api/therapists", apiBase());
      if (search) url.searchParams.set("search", search);
      const res = await fetch(url);
      if (!res.ok) throw new Error("Failed to fetch therapists");
      return res.json();
    },
  });

export const therapistQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["therapists", id],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/therapists/${id}`));
      if (!res.ok) throw new Error("Therapist not found");
      return res.json();
    },
  });

export const therapistDocumentsQueryOptions = (id: string) =>
  queryOptions({
    queryKey: ["therapists", id, "documents"],
    queryFn: async () => {
      const res = await fetch(apiUrl(`/api/therapists/${id}/documents`));
      if (!res.ok) throw new Error("Failed to fetch documents");
      return res.json();
    },
  });

export interface FeaturedTherapist {
  id: string;
  slug?: string | null;
  userName: string;
  userImage: string | null;
  profileImageUrl: string;
  specializations: string;
  problemTags: MatchingProblemTag[];
  yearsExperience: number;
  bio: string;
  sessionCount: number;
  avgRating: number | null;
}

export const featuredTherapistsQuery = queryOptions({
  queryKey: ["featured-therapists"] as const,
  queryFn: async (): Promise<FeaturedTherapist[]> => {
    const res = await fetch(apiUrl("/api/therapists/featured"));
    if (!res.ok) return [];
    return res.json();
  },
});

import type { TherapistListItem } from "@/types";

/**
 * SSR-safe public therapists list query. Uses a stable query key that matches
 * the dehydration allowlist prefix ["therapists", "list"]. No filters — bots
 * and crawlers see the full unfiltered list; client-side filters apply on top.
 */
export const publicTherapistsListQuery = queryOptions({
  queryKey: ["therapists", "list"] as const,
  queryFn: async (): Promise<TherapistListItem[]> => {
    const res = await fetch(apiUrl("/api/therapists"));
    if (!res.ok) return [];
    return res.json();
  },
});
